import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  User,
  Loader2,
  CheckCircle2,
  MapPin,
  Briefcase,
  Building2,
  Crown,
  CreditCard,
  Trash2,
  AlertTriangle,
  ExternalLink,
  Users,
  Copy,
  Check,
  MessageCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = 'https://klarvi.org';

function ReferralSection() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.referral_code) loadStats();
  }, [user]);

  const loadStats = async () => {
    try {
      const res = await authFetch(`${API_URL}/api/referral/stats`);
      if (res.ok) setStats(await res.json());
    } catch { /* non-blocking */ }
  };

  const referralLink = user?.referral_code ? `${FRONTEND_URL}/r/${user.referral_code}` : '';
  const whatsappText = `¡Únete a Klarvi y descubre tus derechos laborales! Regístrate con mi link: ${referralLink}`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user?.referral_code) {
    return (
      <Card id="referral-section" data-testid="referral-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-teal-500" />
            Programa de referidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            Cargando tu link de referido...
          </div>
        </CardContent>
      </Card>
    );
  }

  const freeDaysEarned = (stats?.bonus_days || 0) + (stats?.credit_days || 0);

  return (
    <Card id="referral-section" data-testid="referral-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Users className="w-5 h-5 text-teal-500" />
          Programa de referidos
        </CardTitle>
        <CardDescription>Invita amigos y gana beneficios en tu suscripción</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Referral link */}
        <div>
          <Label className="text-sm font-medium mb-2 block">Tu link de referido</Label>
          <div className="flex gap-2">
            <Input value={referralLink} readOnly className="text-sm font-mono bg-muted" data-testid="referral-link-input" />
            <Button variant="outline" size="sm" onClick={handleCopy} className="flex-shrink-0" data-testid="copy-referral-btn">
              {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            </Button>
            <Button asChild variant="outline" size="sm" className="flex-shrink-0 border-green-300 text-green-700 hover:bg-green-50" data-testid="whatsapp-share-btn">
              <a href={whatsappShareUrl} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="total-referrals">{stats ? stats.total_referrals : '—'}</p>
            <p className="text-xs text-muted-foreground">Referidos totales</p>
          </div>
          <div className="p-3 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="monthly-referrals">{stats ? stats.monthly_active : '—'}</p>
            <p className="text-xs text-muted-foreground">Pro Mensual activos</p>
          </div>
          <div className="p-3 bg-muted rounded-lg text-center">
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="annual-referrals">{stats ? stats.annual_active : '—'}</p>
            <p className="text-xs text-muted-foreground">Pro Anual activos</p>
          </div>
        </div>

        {/* Earnings */}
        {stats && (
          <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg text-sm text-teal-800 space-y-1" data-testid="referral-earnings">
            <p>
              Descuento mensual actual: <strong>{stats.monthly_discount_pct}%</strong>
            </p>
            {freeDaysEarned > 0 && (
              <p data-testid="referral-free-days">
                Días de Pro gratis ganados: <strong>{freeDaysEarned}</strong>
              </p>
            )}
            {stats.monthly_discount_pct === 0 && freeDaysEarned === 0 && (
              <p className="text-teal-700">Aún no tienes descuentos ni días gratis. ¡Comparte tu link para empezar a ganar!</p>
            )}
          </div>
        )}

        {/* Rules */}
        <Separator />
        <div className="text-sm text-muted-foreground leading-relaxed space-y-1" data-testid="referral-rules">
          <p className="font-medium text-foreground mb-2">¿Cómo funciona?</p>
          <p>
            Comparte tu link. Si tu amigo se hace Pro Anual, te regalamos un mes de Pro. Si se hace Pro Mensual, te descontamos 10% de tu mensualidad por cada mes que se mantenga activo. Si cancela, el descuento se detiene el siguiente mes. Trae varios amigos y los descuentos se suman hasta que tu Pro quede gratis. El premio se aplica unos días después del pago de tu amigo, para confirmar que el pago quedó en firme.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}


const industries = [
  { value: 'tecnologia', label: 'Tecnología' },
  { value: 'finanzas', label: 'Finanzas' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'recursos_humanos', label: 'Recursos Humanos' },
  { value: 'ventas', label: 'Ventas' },
  { value: 'operaciones', label: 'Operaciones' },
  { value: 'legal', label: 'Legal' },
  { value: 'salud', label: 'Salud' },
  { value: 'educacion', label: 'Educación' },
  { value: 'manufactura', label: 'Manufactura' },
  { value: 'otro', label: 'Otro' },
];

const seniorities = [
  { value: 'junior', label: 'Junior (0-2 años)' },
  { value: 'mid', label: 'Mid-Level (3-5 años)' },
  { value: 'senior', label: 'Senior (6-10 años)' },
  { value: 'lead', label: 'Lead / Gerente (10+ años)' },
];

const mexicanStates = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango', 'Guanajuato',
  'Guerrero', 'Hidalgo', 'Jalisco', 'México', 'Michoacán', 'Morelos', 'Nayarit',
  'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí',
  'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas'
];

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (window.location.hash === '#referral-section') {
      document.getElementById('referral-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    country: user?.country || 'México',
    state: user?.state || '',
    city: user?.city || '',
    preferred_language: user?.preferred_language || 'es',
    role_title: user?.role_title || '',
    industry: user?.industry || '',
    seniority: user?.seniority || '',
    salary: user?.salary?.toString() || '',
    main_goal: user?.main_goal || '',
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authFetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          salary: formData.salary ? parseFloat(formData.salary) : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Error al guardar');
      }

      const updatedUser = await response.json();
      updateUser(updatedUser);
      toast.success('Perfil actualizado');
    } catch (error) {
      toast.error(error.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'ELIMINAR') return;
    setDeleteLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/auth/delete-account`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Error al eliminar la cuenta');
      toast.success('Tu cuenta ha sido eliminada permanentemente.');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Error al eliminar la cuenta');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Manrope']">Mi Perfil</h1>
          <p className="mt-2 text-muted-foreground">
            Actualiza tu información para obtener mejores recomendaciones.
          </p>
        </div>

        {/* Referral Section - shown up top so it's always visible */}
        <ReferralSection />

        <form onSubmit={handleSubmit}>
          {/* Plan Card */}
          <Card className="border mb-6" data-testid="profile-plan-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <CreditCard className="w-5 h-5 text-[#2563EB]" />
                Plan Actual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {user?.plan === 'pro' ? (
                    <Badge className="bg-[#2563EB] text-white" data-testid="plan-badge">
                      <Crown className="w-3 h-3 mr-1" />
                      Pro
                    </Badge>
                  ) : (
                    <Badge variant="secondary" data-testid="plan-badge">
                      Gratis
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">
                    {user?.plan === 'pro'
                      ? 'Acceso completo a todas las herramientas'
                      : 'Acceso limitado — actualiza para desbloquear todo'}
                  </span>
                </div>
                {user?.plan !== 'pro' && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => navigate('/precios')}
                    className="bg-[#2563EB] hover:bg-[#2563EB]/90"
                    data-testid="upgrade-btn"
                  >
                    <Crown className="w-3 h-3 mr-1" />
                    Actualizar
                  </Button>
                )}
                {user?.plan === 'pro' && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('https://billing.stripe.com/p/login/dRm4gz6BJ2rpededLj3sI00', '_blank')}
                    data-testid="manage-subscription-btn"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Gestionar suscripción
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Personal Info Card */}
          <Card className="border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <User className="w-5 h-5 text-[#2563EB]" />
                Información Personal
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre Completo</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  data-testid="profile-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="state">Estado</Label>
                  <Select
                    value={formData.state}
                    onValueChange={(v) => updateField('state', v)}
                  >
                    <SelectTrigger data-testid="profile-state-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {mexicanStates.map((state) => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    data-testid="profile-city-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="language">Idioma Preferido</Label>
                <Select
                  value={formData.preferred_language}
                  onValueChange={(v) => updateField('preferred_language', v)}
                >
                  <SelectTrigger data-testid="profile-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Professional Info Card */}
          <Card className="border mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <Briefcase className="w-5 h-5 text-[#2563EB]" />
                Información Profesional
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role_title">Puesto Actual</Label>
                <Input
                  id="role_title"
                  value={formData.role_title}
                  onChange={(e) => updateField('role_title', e.target.value)}
                  placeholder="Ej: Desarrollador de Software"
                  data-testid="profile-role-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industria</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(v) => updateField('industry', v)}
                  >
                    <SelectTrigger data-testid="profile-industry-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map((ind) => (
                        <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seniority">Nivel</Label>
                  <Select
                    value={formData.seniority}
                    onValueChange={(v) => updateField('seniority', v)}
                  >
                    <SelectTrigger data-testid="profile-seniority-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {seniorities.map((sen) => (
                        <SelectItem key={sen.value} value={sen.value}>{sen.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="salary">Salario Mensual (MXN) - Opcional</Label>
                <Input
                  id="salary"
                  type="number"
                  value={formData.salary}
                  onChange={(e) => updateField('salary', e.target.value)}
                  placeholder="45000"
                  data-testid="profile-salary-input"
                />
                <p className="text-xs text-muted-foreground">
                  Esta información es privada y se usa solo para personalizar tus recomendaciones.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card className="border mb-6">
            <CardHeader>
              <CardTitle className="text-base font-['Manrope']">Información de Cuenta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Correo Electrónico</Label>
                <Input value={user?.email || ''} disabled className="bg-muted" />
                <p className="text-xs text-muted-foreground">
                  El correo electrónico no se puede cambiar.
                </p>
              </div>
            </CardContent>
          </Card>

          <Button
            type="submit"
            className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
            disabled={loading}
            data-testid="save-profile-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Guardar Cambios
          </Button>
        </form>

        {/* Danger Zone - Delete Account */}
        <Separator className="my-6" />
        <Card className="border border-red-200 bg-red-50/30" data-testid="delete-account-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700 font-['Manrope'] text-base">
              <AlertTriangle className="w-5 h-5" />
              Zona de peligro
            </CardTitle>
            <CardDescription className="text-red-600/70">
              Esta acción es permanente e irreversible. Se eliminarán tu cuenta y todos tus datos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showDeleteConfirm ? (
              <Button
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-account-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar mi cuenta
              </Button>
            ) : (
              <div className="space-y-3" data-testid="delete-confirm-section">
                <p className="text-sm text-red-700 font-medium">
                  Escribe <span className="font-bold">ELIMINAR</span> para confirmar:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="ELIMINAR"
                  className="max-w-xs border-red-300 focus-visible:ring-red-400"
                  data-testid="delete-confirm-input"
                />
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'ELIMINAR' || deleteLoading}
                    data-testid="delete-confirm-btn"
                  >
                    {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
                    Eliminar permanentemente
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                    data-testid="delete-cancel-btn"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
